import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Banknote, ChevronDown, CreditCard, Loader2, Receipt } from "lucide-react";
import BackOffice from "@/components/account/BackOffice";
import { Btn, Chip, PageHead, Pill, Section } from "@/components/account/Shell";
import api, { formatApiError } from "@/lib/api";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const RANGES = [[30, "30 days"], [90, "90 days"], [180, "6 months"], [365, "12 months"]];
const STATE = {
    open: ["blue", "this week"],
    paid_out: ["green", "paid out"],
    waiting: ["amber", "in your Stripe balance"],
    counter: ["neutral", "you collected it"],
};

function Tile({ icon: Icon, label, value, note, testid }) {
    return (
        <div className="border border-[#d0d0d0] bg-white p-2.5" data-testid={testid}>
            <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-[#4a4a4a]">
                <Icon size={11} /> {label}
            </p>
            <p className="mt-1 text-xl font-bold tabular-nums text-[#1a1a1a]">{value}</p>
            {note && <p className="mt-0.5 text-[12px] text-[#4a4a4a]">{note}</p>}
        </div>
    );
}

function Statement({ s }) {
    const [open, setOpen] = useState(false);
    const [tone, label] = STATE[s.state] || STATE.counter;
    return (
        <div className="border-t border-[#d0d0d0] first:border-t-0" data-testid={`statement-${s.id}`}>
            <button onClick={() => setOpen((v) => !v)} data-testid={`statement-toggle-${s.id}`}
                className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-2.5 py-1.5 text-left hover:bg-[#fafafa]">
                <ChevronDown size={13} className={`shrink-0 ${open ? "rotate-180" : ""}`} />
                <span className="text-[13px] font-bold text-[#1a1a1a]">{s.label}</span>
                <Chip tone={tone} testid={`statement-state-${s.id}`}>{label}</Chip>
                <span className="text-[12px] text-[#4a4a4a]">{s.count} order{s.count === 1 ? "" : "s"}</span>
                <span className="ml-auto flex flex-wrap items-center gap-x-3">
                    <span className="text-[12px] text-[#4a4a4a]">fee {money(s.fee)}</span>
                    <span className="text-[13px] font-bold tabular-nums text-[#1a1a1a]">{money(s.net)}</span>
                </span>
            </button>
            {open && (
                <div className="border-t border-[#d0d0d0] bg-[#fafafa]" data-testid={`statement-rows-${s.id}`}>
                    <div className="grid grid-cols-[6rem_1fr_5rem_5rem_5rem_4.5rem] gap-x-2 border-b border-[#d0d0d0] px-2.5 py-1 text-[11px] font-semibold uppercase text-[#4a4a4a]">
                        <span>Date</span><span>Order</span><span className="text-right">Gross</span>
                        <span className="text-right">Fee</span><span className="text-right">Net</span><span>How</span>
                    </div>
                    {s.rows.map((r) => (
                        <div key={r.order_number}
                            className="grid grid-cols-[6rem_1fr_5rem_5rem_5rem_4.5rem] items-center gap-x-2 border-b border-[#d0d0d0] px-2.5 py-1 text-[13px] text-[#1a1a1a] last:border-b-0"
                            data-testid={`statement-row-${r.order_number}`}>
                            <span className="tabular-nums">{r.date}</span>
                            <span className="min-w-0 truncate font-semibold">{r.order_number} · {r.customer}</span>
                            <span className="text-right tabular-nums">{money(r.gross)}</span>
                            <span className="text-right tabular-nums">{r.fee ? money(r.fee) : "—"}</span>
                            <span className="text-right font-semibold tabular-nums">{money(r.net)}</span>
                            <span className="text-[11px] font-semibold uppercase">{r.method}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function PayoutHistory() {
    const [days, setDays] = useState(180);
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");

    const load = useCallback(() => {
        setData(null);
        api.get("/store/payouts", { params: { days } }).then(({ data: d }) => setData(d))
            .catch((e) => setErr(formatApiError(e.response?.data?.detail) || "Could not build your statements"));
    }, [days]);
    useEffect(load, [load]);

    if (err) return (
        <BackOffice><div className="mx-auto max-w-2xl py-20 text-center" data-testid="payout-history-locked">
            <Receipt size={34} className="mx-auto text-[#4a4a4a]" />
            <h1 className="mt-4 text-xl font-bold text-[#1a1a1a]">Statements come with a space.</h1>
            <p className="mt-2 text-sm text-[#4a4a4a]">{err}</p>
            <Link to="/account/sell" className="mt-5 inline-block"><Btn tone="solid" testid="payout-history-open-space">See the spaces</Btn></Link>
        </div></BackOffice>
    );

    if (!data) return (
        <BackOffice><div className="flex min-h-[40vh] items-center justify-center">
            <Loader2 className="animate-spin text-[#4a4a4a]" /></div></BackOffice>
    );

    const t = data.totals;
    return (
        <BackOffice>
            <PageHead kicker="Insights" title="Payout history" testid="payout-history-head"
                sub="Every finished job you were paid for, grouped by week. Card money runs through your own Stripe account; counter money you already have."
                right={
                    <div className="flex flex-wrap justify-end gap-1.5" data-testid="payout-ranges">
                        {RANGES.map(([d, label]) => (
                            <Pill key={d} active={days === d} testid={`payout-range-${d}`}
                                onClick={() => setDays(d)}>{label}</Pill>
                        ))}
                    </div>
                } />

            <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-4" data-testid="payout-tiles">
                <Tile icon={Banknote} label="Money in" value={money(t.gross)} testid="payout-gross"
                    note={`${t.orders} paid order${t.orders === 1 ? "" : "s"} · last ${data.days} days`} />
                <Tile icon={Receipt} label="13thav's cut" value={money(t.fee)} testid="payout-fee"
                    note={`${data.fee_pct}% of card sales only`} />
                <Tile icon={CreditCard} label="Through Stripe" value={money(t.card)} testid="payout-card"
                    note={data.status.payouts_enabled ? "payouts switched on" : "connect Stripe to get paid out"} />
                <Tile icon={Banknote} label="Collected at the counter" value={money(t.counter)}
                    testid="payout-counter" note="cash, Venmo, Zelle — never touched Stripe" />
            </div>

            <div className="mt-2">
                <Section title="Weekly statements" pad={false} testid="payout-statements"
                    note={`${data.statements.length} week${data.statements.length === 1 ? "" : "s"} with money in`}>
                    {data.statements.length ? data.statements.map((s) => <Statement key={s.id} s={s} />) : (
                        <p className="p-3 text-sm text-[#4a4a4a]" data-testid="payout-empty">
                            Nothing paid yet in this window. A job counts here the moment it's marked paid.
                        </p>
                    )}
                </Section>
            </div>

            <div className="mt-2">
                <Section title="Stripe payouts" testid="payout-stripe"
                    note={data.status.house ? "13thav's own register" : data.status.connected ? "your connected account" : "not connected"}
                    right={<Link to="/account/payouts" data-testid="payout-go-connect"><Btn>Payout settings</Btn></Link>}>
                    {data.stripe_payouts.length ? (
                        <div className="divide-y divide-[#d0d0d0]">
                            {data.stripe_payouts.map((p) => (
                                <div key={p.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-1.5 text-[13px]"
                                    data-testid={`stripe-payout-${p.id}`}>
                                    <span className="font-semibold tabular-nums text-[#1a1a1a]">{money(p.amount)}</span>
                                    <span className="text-[#4a4a4a]">{p.currency} · {p.method}</span>
                                    <Chip tone={p.status === "paid" ? "green" : "amber"}>{p.status}</Chip>
                                    <span className="ml-auto text-[12px] text-[#4a4a4a]">arrives {p.arrival}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-[13px] text-[#4a4a4a]" data-testid="payout-stripe-empty">
                            {data.status.payouts_enabled
                                ? "No Stripe payout has landed yet — the statements above are the money waiting to go out."
                                : "Connect your Stripe account and card money starts paying out to your bank on Stripe's schedule. Counter money never runs through it."}
                        </p>
                    )}
                </Section>
            </div>
        </BackOffice>
    );
}
